// @ts-check
import React, { useMemo } from 'react';
import { Pagination } from '@patternfly/react-core';
import { useTranslation } from 'localization/useTranslation';
/**
 * @callback onSetPageCallback
 * @param {MouseEvent | React.MouseEvent<Element> | React.KeyboardEvent<Element>} event
 * @param {number} newPage
 * @param {number} perPage
 * @param {number} startIdx
 * @param {number} endIdx
 * @returns {void}
 */

/**
 * @callback onPerPageSelectCallback
 * @param {MouseEvent | React.MouseEvent<Element> | React.KeyboardEvent<Element>} event
 * @param {number} newPerPage
 * @param {number} newPage
 * @param {number} startIdx
 * @param {number} endIdx
 * @returns {void}
 */

/**
 * @typedef {Object} PageInfo
 * @property {number} page
 * @property {number} perPage
 * @property {onSetPageCallback} onSetPage
 * @property {onPerPageSelectCallback} onPerPageSelect
 * @property {number} itemCount
 */

/**
 * @typedef {Object} LocalizedPaginationProps
 * @prop {PageInfo} [pageInfo]
 * @prop {'top' | 'bottom'} [variant]
 */

/**
 * Localized version of Pagination
 * @param {LocalizedPaginationProps} props
 * @returns {JSX.Element}
 */
const LocalizedPagination = ({ pageInfo, variant }) => {
  const T = useTranslation();
  const titles = useMemo(() => ({
    currPageAriaLabel: T.common.list.pagination.currPageAriaLabel,
    items: T.common.list.pagination.items,
    itemsPerPage: T.common.list.pagination.itemsPerPage,
    ofWord: T.common.list.pagination.ofWord,
    optionsToggleAriaLabel: T.common.list.pagination.optionsToggleAriaLabel,
    page: T.common.list.pagination.page,
    pages: T.common.list.pagination.pages,
    paginationAriaLabel: T.common.list.pagination.paginationAriaLabel,
    perPageSuffix: T.common.list.pagination.perPageSuffix,
    toFirstPageAriaLabel: T.common.list.pagination.toFirstPageAriaLabel,
    toLastPageAriaLabel: T.common.list.pagination.toLastPageAriaLabel,
    toNextPageAriaLabel: T.common.list.pagination.toNextPageAriaLabel,
    toPreviousPageAriaLabel: T.common.list.pagination.toPreviousPageAriaLabel
  }), [T.common.list.pagination.currPageAriaLabel, T.common.list.pagination.items, T.common.list.pagination.itemsPerPage, T.common.list.pagination.ofWord, T.common.list.pagination.optionsToggleAriaLabel, T.common.list.pagination.page, T.common.list.pagination.pages, T.common.list.pagination.paginationAriaLabel, T.common.list.pagination.perPageSuffix, T.common.list.pagination.toFirstPageAriaLabel, T.common.list.pagination.toLastPageAriaLabel, T.common.list.pagination.toNextPageAriaLabel, T.common.list.pagination.toPreviousPageAriaLabel]);

  return <Pagination {...pageInfo} variant={variant} titles={titles} />;
};

export default React.memo(LocalizedPagination);
